import { STRING_TRUE } from '@/data/constants/catalog';
import { SearchProfiles } from '@/data/constants/commonIntegrationConstants';
import { DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC, DATA_KEY_PRODUCT } from '@/data/constants/dataKey';
import { EMS_STORE_FEATURE } from '@/data/constants/flexFlowStoreFeature';
import { FULFILLMENT_METHOD } from '@/data/constants/inventory';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { TYPES } from '@/data/constants/product';
import { useAllowableShippingModes } from '@/data/Content/_AllowableShippingModes';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useLoginRedirectRequired } from '@/data/Content/_LoginRedirectRequired';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { getPromo, usePromo } from '@/data/Content/_Promos';
import { requisitionListItemUpdate } from '@/data/Content/_RequisitionList';
import { wishListUpdater } from '@/data/Content/_WishListDetails';
import { fetchCreateNewWishlist, fetchDefaultWishlistOrCreateNew } from '@/data/Content/_Wishlists';
import { useWishRequisitionList } from '@/data/Content/_WishRequisitionList';
import { useBreadcrumbTrail } from '@/data/Content/BreadcrumbTrail';
import {
	addToCartFetcherV2 as addToCartFetcher,
	BASE_ADD_2_CART_BODY,
	useCartSWRKey,
} from '@/data/Content/Cart';
import { useCategory } from '@/data/Content/Category';
import { getDefaultStore, useDefaultStore } from '@/data/Content/DefaultStore';
import { getFlexFlowStoreFeature } from '@/data/Content/FlexFlowStoreFeature-Server';
import { useInventoryV2 } from '@/data/Content/InventoryV2';
import { personMutatorKeyMatcher } from '@/data/Content/Login';
import { useNotifications } from '@/data/Content/Notifications';
import { getProduct, useProduct } from '@/data/Content/Product';
import { getStoreLocale } from '@/data/Content/StoreLocale-Server';
import { useEmarsys } from '@/data/context/emarsysCtx';
import { EventsContext } from '@/data/context/events';
import { StoreInventoryContext } from '@/data/context/storeInventory';
import { getGTMConfig } from '@/data/events/handlers/gtm';
import { getLocalization, useLocalization } from '@/data/Localization';
import { isB2BStore, useSettings } from '@/data/Settings';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { ContentProps } from '@/data/types/ContentProps';
import { Attachment, ProductType, Selection } from '@/data/types/Product';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import { StoreDetails } from '@/data/types/Store';
import { useUser } from '@/data/User';
import { getStoreDataByPhysicalStoreId } from '@/data/utils/commonIntegrationUtils';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { getCurrencyFromContext } from '@/data/utils/getCurrencyFromContext';
import { getParentCategoryFromSlashPath } from '@/data/utils/getParentCategoryFromSlashPath';
import { HDMMapProductDetailsData } from '@/data/utils/hdmMapProductDetailsData';
import { getIsProductExcluded } from '@/data/utils/isProductExcluded';
import { logEventWithValues } from '@/data/utils/logEventsUtils';
import { getAttrsByIdentifier, search } from '@/data/utils/mapProductDetailsData';
import { cartMutatorKeyMatcher } from '@/data/utils/mutatorKeyMatchers/cartMutatorKeyMatcher';
import { processError } from '@/data/utils/processError';
import type { WishlistWishlistItem } from 'integration/generated/transactions/data-contracts';
import { getProductPromotions } from 'presentation/events/utils/productUtils';
import {
	MouseEvent,
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import {
	MARKET_BUYABLE_EXCLUDE,
	PRODUCT_SKU_STRING_PART,
	STORE_BUYABLE_EXCLUDE,
} from 'shared/custom/constants/commonConstants';
import {
	INVENTORY_SEARCH_DB,
	INVENTORY_SEARCH_PDP,
} from 'shared/custom/constants/inventoryConstants';
import { mutate } from 'swr';

type Props = {
	partNumber: string;
	/** @deprecated use `physicalStore` instead */
	physicalStoreName?: string;
	// HDM: PhysicalStore is not utilized, need revise to combine with StoreDefault
	physicalStore?: StoreDetails;
	physicalStoreIds?: string[];
};

const fetchLocalization = async ({ cache, context }: ContentProps, productId: string) => {
	const { localeName: locale } = await getStoreLocale({ cache, context });
	await Promise.all([
		getPromo(cache, productId, context),
		getLocalization(cache, locale, 'productDetail'),
		getLocalization(cache, locale, 'CommerceEnvironment'),
		getLocalization(cache, locale, 'PriceDisplay'),
		getLocalization(cache, locale, 'Common'),
		getLocalization(cache, locale, 'Inventory'),
		getLocalization(cache, locale, 'Subscriptions'),
	]);
};

// Preserve the color code hash (e.g. #colorCode:130) across a SKU redirect,
// otherwise the new PDP loses the selection and falls back to its default color.
const withCurrentColorHash = (href: string) => {
	const hash = typeof window !== 'undefined' ? window.location.hash : '';
	return hash ? `${href}${hash}` : href;
};

/** Server side to fetch product details
 * For item type, the cache key is:
 *   Item ->  partNumber: <partNumber>
 *   Parent product -> id:<parentCatentryId>
 * For product type, the cache key is:
 *    partNumber:<partNumber>
 */

export const getProductDetails = async ({ cache, id, context }: ContentProps) => {
	const partNumber = id.toString();
	const { physicalStoreId, uniqueId: stLocId } = await getDefaultStore(cache, context);
	const { marketId } = getStoreDataByPhysicalStoreId(physicalStoreId);
	const product = await getProduct(
		cache,
		partNumber,
		context,
		SearchProfiles.HDM_FIND_PRODUCT_BY_PART_NUMBER_DETAILS,
		marketId,
		stLocId
	);
	await getFlexFlowStoreFeature({
		cache,
		id: EMS_STORE_FEATURE.GUEST_SHOPPING,
		context,
	});
	await fetchLocalization({ cache, id, context }, product?.id ?? '');
};

// Client side to get product details
export const useProductDetails = (props: Props) => {
	// const { onProductView, onAddToCart, onAddToWishlist } = useContext(EventsContext);
	const { onAddToWishlist } = useContext(EventsContext);
	const { breadcrumb } = useBreadcrumbTrail();
	const { dispatch } = useEmarsys();
	const { showSuccessMessage, showErrorMessage, notifyError, showMessage } = useNotifications();
	const { physicalStoreId: currentStoreId } = useDefaultStore();
	const wlNls = useLocalization('WishList');
	const [attachments, setAttachments] = useState<Attachment[]>([]);
	const [isBuyable, setIsBuyable] = useState<boolean>(true);
	const success = useLocalization('success-message');
	const errorMessages = useLocalization('error-message');
	const { settings } = useSettings();
	const isB2B = isB2BStore(settings);
	const { user } = useUser();
	const currency = getCurrencyFromContext(user?.context);
	const router = useNextRouter();
	const { langId } = getClientSideCommon(settings, router);
	const params = useExtraRequestParameters();
	const { partNumber: pnFromPage, physicalStoreIds, physicalStore } = props;
	const { product: inputCE, loading: isLoadingRoot } = useProduct({
		id: pnFromPage,
		profileName: SearchProfiles.HDM_FIND_PRODUCT_BY_PART_NUMBER_DETAILS,
	});
	const { loginStatus, redirectToLoginIfNeed: loginRequired } = useLoginRedirectRequired();
	// Fetch parent proudct, store in different variable
	const { product: root } = useProduct({
		id: inputCE?.parentCatalogEntryID,
		isCEId: true,
		condition: inputCE?.type === 'item',
		profileName: SearchProfiles.HDM_FIND_PRODUCT_BY_IDS_DETAILS,
	});
	useEffect(() => {
		if (inputCE?.attachments && attachments.length === 0) {
			setAttachments(inputCE?.attachments);
		}
	}, [inputCE?.attachments, attachments]);
	// For the one who has parent, selection is current item, product is parent product.
	// For the one who has no parent, selection is identical to product.
	const { selection: initial, product } = useMemo(
		() => HDMMapProductDetailsData(inputCE, root),
		[inputCE, root]
	);
	const [selection, setSelection] = useState<Selection>(initial as Selection);
	useEffect(() => {
		setSelection(initial as Selection);
	}, [initial]);
	useEffect(() => {
		const sendEmarsysTags = () => {
			const arrayCategories = breadcrumb.filter((e) => e.type !== DATA_KEY_PRODUCT);
			const categories = arrayCategories.map((tag) => tag.label).join('>');
			if (!product?.partNumber) return;
			const query = {
				view: ['view', product.partNumber.replace(PRODUCT_SKU_STRING_PART, '')],
				exclude: ['exclude', 'category', 'has', categories],
			};
			dispatch({ type: 'EMARSYS_PDP_TAG_DATA', payload: query });
		};
		if (product?.partNumber) {
			sendEmarsysTags();
		}
	}, [product?.partNumber, dispatch, breadcrumb]);
	const { ga4, ua } = getGTMConfig(settings);
	const { category } = useCategory(
		ga4 || ua ? getParentCategoryFromSlashPath(product?.parentCatalogGroupID) : ''
	);
	const { promos } = usePromo(
		product?.type === 'product' && selection?.sku ? selection?.sku?.id : product?.id
	);
	const currentCartSWRKey = useCartSWRKey(); // in current language
	const isGenericUser = user?.isGeneric ?? false;
	// change part number to product id
	// Fetch all children inventory
	const productIdsForInventory = useMemo(() => {
		switch (product?.type) {
			case TYPES.sku:
				return product?.id;
			case TYPES.product:
				return product?.items?.map((item: ProductType) => item?.id).join(',');
			case TYPES.kit:
				return product?.components?.map((item: ProductType) => item?.uniqueID).join(',');
			default:
				return undefined;
		}
	}, [product]);
	const inventorySearchType =
		product?.type === TYPES.product && product.partNumber.includes('_SS')
			? INVENTORY_SEARCH_DB
			: INVENTORY_SEARCH_PDP;

	const {
		availability,
		hasInventory,
		isLoading: isInventoryLoading,
	} = useInventoryV2({
		productIds: productIdsForInventory,
		physicalStoreIds,
		search: inventorySearchType,
	});

	const { pickupInStoreShipMode, deliveryShipMode } = useAllowableShippingModes();
	const { wishLists, requisitionLists } = useWishRequisitionList();
	const { setOpen } = useContext(StoreInventoryContext);
	const isMobileApp = settings.isMobileApp;

	const [isDeliverySelected, setIsDeliverySelected] = useState<boolean>(() => !!deliveryShipMode);

	const selectBox = useCallback(
		(name: string) => (_event: MouseEvent<HTMLElement>) =>
			setIsDeliverySelected(name !== FULFILLMENT_METHOD.PICKUP),
		[]
	);

	const onSelectStore = useCallback(
		(event: MouseEvent<HTMLElement>) => {
			event.stopPropagation();
			setOpen(true);
		},
		[setOpen]
	);

	/**
	 * Defining attribute change handler
	 * @param identifier new attribute identifier
	 * @param value new attribute value
	 */
	const onAttributeChange = useCallback(
		(identifier: string, value: string) => {
			if (!product?.items) return;

			const items = product.items.filter(
				(s) =>
					!getIsProductExcluded({
						product: s,
						physicalStoreId: currentStoreId,
						isB2B,
					})
			);
			if (!items.length) return;

			const asAttrs = items.map((s) => getAttrsByIdentifier(s.definingAttributes));
			const fullAttr = {
				...(selection ?? initial).attrsByIdentifier,
				[identifier]: value,
			};
			const { exact, best } = search(fullAttr, identifier, asAttrs);
			let sku: ProductType | undefined = undefined;
			let attrsByIdentifier: Record<string, string> | undefined = undefined;

			if (exact !== -1) {
				attrsByIdentifier = fullAttr;
				sku = items[exact];
			} else if (best !== -1) {
				attrsByIdentifier = { ...asAttrs[best] };
				sku = items[best];
			} else {
				sku = {} as ProductType;
			}

			if (sku?.seo?.href && router.asPath !== sku.seo.href) {
				// Use router.push(url, as, options) to ensure shallow routing works correctly
				// url: the page in /pages to render
				// as: the path to show in the browser
				router.push(router.pathname, withCurrentColorHash(sku.seo.href), { shallow: false });
			}

			setSelection({
				...(selection ?? initial),
				buyable: !!(sku?.buyable === STRING_TRUE),
				sku,
				attrsByIdentifier,
			});
		},
		[currentStoreId, initial, isB2B, product?.items, router, selection]
	);

	const onQuantity = useCallback(
		(quantity: number | null) =>
			setSelection((selection) => ({
				...(selection ?? initial),
				quantity: quantity ?? 1,
			})),
		[initial]
	);

	const validatedAvailability = useCallback(
		() =>
			isDeliverySelected
				? availability?.find((a) => a.status)
				: availability?.find((a) => a.physicalStoreId),
		[availability, isDeliverySelected]
	);

	const constructOrderItem = useCallback(
		(availability: ProductAvailabilityData) => {
			const { sku, quantity } = selection ?? initial;
			return {
				partNumber: sku?.partNumber,
				quantity: quantity.toString(),
				...(isDeliverySelected
					? { shipModeId: deliveryShipMode?.shipModeId }
					: {
							physicalStoreId: availability.physicalStoreId,
							shipModeId: pickupInStoreShipMode?.shipModeId,
					  }),
			};
		},
		[deliveryShipMode, initial, isDeliverySelected, pickupInStoreShipMode, selection]
	);
	const addToCart = useCallback(
		async (event: MouseEvent<HTMLElement>) => {
			if (await loginRequired()) {
				return;
			}
			event.preventDefault();
			event.stopPropagation();
			const { sku } = selection ?? initial;
			if (sku?.partNumber) {
				// the availability of online or physical store
				const avail = validatedAvailability();
				if (!isDeliverySelected && !avail) {
					showErrorMessage(errorMessages.SelectStoreToAddToCart.t());
					return;
				}

				const orderItem = constructOrderItem(avail as ProductAvailabilityData);

				const data = { ...BASE_ADD_2_CART_BODY, orderItem: [orderItem] };
				try {
					await addToCartFetcher(isGenericUser)(settings?.storeId ?? '', {}, data, params);
					if (isGenericUser) {
						await mutate(personMutatorKeyMatcher(EMPTY_STRING)); // current page
						await mutate(
							personMutatorKeyMatcher(DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC),
							undefined
						);
					}
					await mutate(cartMutatorKeyMatcher(EMPTY_STRING));
					await mutate(cartMutatorKeyMatcher(currentCartSWRKey), undefined); // cart in other languages
					// notification
					showSuccessMessage(success.ITEM_TO_CART.t([product?.name ?? '']), true);

					scrollTo(0, 0);
				} catch (e) {
					notifyError(processError(e as TransactionErrorResponse));
				}
			}
		},
		[
			loginRequired,
			selection,
			initial,
			validatedAvailability,
			isDeliverySelected,
			constructOrderItem,
			showErrorMessage,
			errorMessages,
			isGenericUser,
			settings,
			params,
			showSuccessMessage,
			success,
			product,
			notifyError,
			currentCartSWRKey,
		]
	);

	const addToWishList = useCallback(
		(
				partNumber: string,
				quantity: number,
				wishList: WishlistWishlistItem,
				addCustomMessage?: ReactElement | string,
				customIcon?: ReactElement,
				notHide?: boolean
			) =>
			async () => {
				try {
					const rc = await wishListUpdater(true)(
						settings?.storeId as string,
						wishList.uniqueID as string,
						{ item: [{ partNumber, quantityRequested: quantity.toString() } as any] },
						{ addItem: true },
						params
					);
					if (rc) {
						if (isMobileApp && wishList.uniqueID) {
							logEventWithValues('success_add_to_wish_list', wishList.uniqueID);
						} else {
							addCustomMessage
								? showMessage(addCustomMessage, {
										severity: 'success',
										icon: customIcon,
										doesNotHide: notHide,
										isWishList: true,
								  })
								: showSuccessMessage(
										!!addCustomMessage
											? addCustomMessage
											: success.WISHLIST_ADD_SUCCESS.t([wishList.description as string])
								  );
						}
					}
					product && onAddToWishlist(product, quantity);
				} catch (e) {
					notifyError(processError(e as TransactionErrorResponse));
				}
			},
		[
			settings?.storeId,
			params,
			product,
			onAddToWishlist,
			isMobileApp,
			showMessage,
			showSuccessMessage,
			success.WISHLIST_ADD_SUCCESS,
			notifyError,
		]
	);

	const addToDefaultWishlist = useCallback(
		(partNumber: string, quantity: number) => async () => {
			try {
				const { wishlistId, wishlistName } = await fetchDefaultWishlistOrCreateNew(true)(
					settings?.storeId,
					undefined,
					wlNls.DefaultWishListName.t(),
					params
				);
				addToWishList(partNumber, quantity, {
					uniqueID: wishlistId,
					description: wishlistName,
				} as WishlistWishlistItem)();
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			}
		},
		[wlNls, settings?.storeId, params, addToWishList, notifyError]
	);

	const addToNewWishlist = useCallback(
		(partNumber: string, quantity: number, listName: string) => async () => {
			try {
				const { wishlistId } = await fetchCreateNewWishlist(true)(
					settings?.storeId as string,
					undefined,
					listName,
					params
				);
				addToWishList(partNumber, quantity, {
					uniqueID: wishlistId,
					description: listName,
				} as WishlistWishlistItem)();
			} catch (e) {
				notifyError(processError(e as TransactionErrorResponse));
			}
		},
		[settings?.storeId, params, addToWishList, notifyError]
	);

	const addToRequisitionList = useCallback(
		(requisitionListId: string) => async () => {
			const { sku, quantity } = selection ?? initial;
			if (sku?.partNumber) {
				try {
					await requisitionListItemUpdate(true)(
						{
							storeId: settings.storeId,
							langId,
							requisitionListId,
							data: { partNumber: sku.partNumber, quantity },
						},
						params
					);
					showSuccessMessage(success.addItemListSuccessfully.t({ v: sku.name }));
				} catch (e) {
					notifyError(processError(e as TransactionErrorResponse));
				}
			}
		},
		[initial, notifyError, params, selection, settings.storeId, showSuccessMessage, success, langId]
	);

	const productCurrency = product?.productPrice.currency;

	useEffect(() => {
		if (selection?.sku) {
			const promotions = getProductPromotions({
				productAttributes: selection?.sku?.attributes ?? [],
				physicalStoreId: currentStoreId,
			});
			setIsBuyable(
				![STORE_BUYABLE_EXCLUDE, MARKET_BUYABLE_EXCLUDE].some((attr) => promotions.includes(attr))
			);
		}
		if (!isLoadingRoot && !inputCE) {
			setIsBuyable(false);
		}
	}, [selection?.sku, currentStoreId, isLoadingRoot, inputCE]);
	useEffect(() => {
		setSelection(initial as Selection);
	}, [initial, langId, currency, productCurrency]);

	useEffect(() => {
		setIsDeliverySelected(!!deliveryShipMode);
	}, [deliveryShipMode]);

	return {
		isLoading: isLoadingRoot,
		attachments,
		onQuantity,
		selection: selection ?? initial,
		isBuyable,
		product,
		availability,
		onAttributeChange,
		addToWishList,
		addToCart,
		hasInventory,
		loginStatus,
		promos,
		wishLists,
		loginRequired,
		pickupInStoreShipMode,
		requisitionLists,
		addToRequisitionList,
		category,
		addToDefaultWishlist,
		addToNewWishlist,
		isInventoryLoading,
		isDeliverySelected,
		setIsDeliverySelected,
		selectBox,
		physicalStore,
		onSelectStore,
		deliveryShipMode,
	};
};
export { getAttrsByIdentifier };
